import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { Robot, Flag, ArrowsClockwise, ChatCircleText, Warning } from "@phosphor-icons/react";

/**
 * Coach AI Log — every Q&A between clients and Coach Jimmy AI, most recent
 * first. Coach can flag any answer that went off-message for later review.
 */
export default function CoachAILog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [flagging, setFlagging] = useState(null); // id being flagged

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/coach/coach-ai/log?limit=100${flaggedOnly ? "&flagged_only=true" : ""}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setFlaggedCount(data.flagged_count || 0);
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [flaggedOnly]);

  const toggleFlag = async (id, next) => {
    setFlagging(id); setErr("");
    try {
      await http.patch(`/coach/coach-ai/${id}/flag`, { flagged: next });
      setItems((cur) => cur.map((it) => it.id === id ? { ...it, coach_flagged: next } : it));
      setFlaggedCount((n) => n + (next ? 1 : -1));
    } catch (e) { setErr(apiError(e, "Flag update failed.")); }
    finally { setFlagging(null); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="coach-ai-log-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="id-label flex items-center gap-2 mb-1">
            <Robot size={12} weight="fill" /> Coach Jimmy AI
          </div>
          <h1 className="id-h1">AI Q&amp;A Log</h1>
          <p className="text-zinc-400 font-manrope text-sm mt-1">
            Every question your clients asked Coach Jimmy AI, plus the answer they got.
            Flag any reply that went off-message so we can tighten the prompt.
          </p>
        </div>
        <button
          onClick={load}
          className="id-btn-ghost text-xs shrink-0"
          data-testid="coach-ai-log-refresh"
        >
          <ArrowsClockwise size={12} weight="bold" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="id-card">
          <div className="id-label text-zinc-400 mb-1">Total questions</div>
          <div className="font-oswald text-4xl text-white" data-testid="ai-log-total">{total}</div>
        </div>
        <div className="id-card">
          <div className="id-label text-zinc-400 mb-1">Flagged</div>
          <div className={`font-oswald text-4xl ${flaggedCount > 0 ? "text-[#FF3333]" : "text-white"}`} data-testid="ai-log-flagged">
            {flaggedCount}
          </div>
        </div>
        <div className="id-card md:col-span-2 flex items-center">
          <label className="flex items-center gap-2 cursor-pointer text-sm font-manrope">
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.target.checked)}
              className="accent-[#FF3333]"
              data-testid="ai-log-flagged-only"
            />
            <span className="text-zinc-300">Show only flagged answers</span>
          </label>
        </div>
      </div>

      {err && (
        <div className="border border-[#FF3333]/60 bg-[#FF3333]/10 text-[#FF9999] px-3 py-2 text-xs font-manrope" data-testid="ai-log-error">
          {err}
        </div>
      )}

      {loading && <div className="id-label text-zinc-500 id-pulse">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="id-card text-center py-12" data-testid="ai-log-empty">
          <ChatCircleText size={40} weight="thin" className="mx-auto mb-3 text-zinc-700" />
          <p className="text-zinc-400 font-manrope">
            {flaggedOnly ? "No flagged answers." : "No AI questions yet — clients haven't tried it."}
          </p>
        </div>
      )}

      <div className="space-y-3" data-testid="ai-log-items">
        {items.map((it) => (
          <div
            key={it.id}
            className={`id-card ${it.coach_flagged ? "border-[#FF3333]" : ""}`}
            data-testid={`ai-log-item-${it.id}`}
          >
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-xs font-manrope text-zinc-400">
                  <span className="text-white font-semibold">{it.client_name || it.client_email}</span>
                  <span className="text-zinc-600"> · {it.client_email}</span>
                </div>
                <div className="text-[10px] font-manrope text-zinc-600 uppercase tracking-[0.15em] mt-0.5">
                  {new Date(it.created_at).toLocaleString()}
                </div>
              </div>
              <button
                onClick={() => toggleFlag(it.id, !it.coach_flagged)}
                disabled={flagging === it.id}
                className={`text-xs px-2 py-1 border transition-colors shrink-0 uppercase tracking-[0.1em] font-bold flex items-center gap-1 ${
                  it.coach_flagged
                    ? "border-[#FF3333] bg-[#FF3333]/20 text-[#FF3333]"
                    : "border-[#262626] text-zinc-500 hover:border-[#FF3333] hover:text-[#FF3333]"
                } disabled:opacity-50`}
                data-testid={`ai-log-flag-${it.id}`}
              >
                {it.coach_flagged ? <Warning size={12} weight="fill" /> : <Flag size={12} weight="bold" />}
                {it.coach_flagged ? "Flagged" : "Flag"}
              </button>
            </div>

            <div className="space-y-2 font-manrope text-sm">
              <div className="border-l-2 border-[#FF3333]/60 pl-3 py-1">
                <div className="id-label text-zinc-500 mb-1">Question</div>
                <p className="text-white whitespace-pre-wrap">{it.question}</p>
              </div>
              <div className="border-l-2 border-zinc-700 pl-3 py-1">
                <div className="id-label text-zinc-500 mb-1">Coach AI Answer</div>
                <p className="text-zinc-300 whitespace-pre-wrap">{it.answer}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
